export const PRIVACY_LAST_UPDATED = "October 2, 2026";

export const privacyPolicyContent = {
  en: {
    tagline: "Your privacy",
    title: "Privacy Policy",
    intro:
      "Drive Now Autohaus (“we,” “us,” or “our”) respects your privacy. This Privacy Policy explains what personal information we collect on this website and in connection with vehicle sales, financing, trade-ins, and service; how we use it; who we share it with; and the choices you have. By using this website or submitting a form, you acknowledge this Policy.",
    dealerBlock: [
      "Drive Now Autohaus",
      "5401 W Cermak Rd, Cicero, IL 60804",
      "Phone: (708) 222-6100",
    ],
    sections: [
      {
        title: "1. Scope",
        paragraphs: [
          "This Policy applies to our public website, online inventory, financing and pre-qualification forms, trade-in submissions, service scheduling, customer-information requests, electronic document signing, SMS/phone communications, and related dealership operations.",
          "It does not apply to third-party websites we link to, including lender sites, manufacturer sites, Google, YouTube, Meta, or payment processors. Those companies have their own policies.",
        ],
      },
      {
        title: "2. Personal information we collect",
        paragraphs: [
          "We collect information that identifies, relates to, or could reasonably be linked to you. Depending on how you use our services, that may include:",
        ],
        bullets: [
          "Identifiers: name, email, phone, postal address, city, state, ZIP, IP address, and account or request IDs.",
          "Government and identity data you choose to provide: date of birth, driver’s license number, issuing state/county, expiration date, and Social Security number (typically for credit applications).",
          "Commercial and vehicle data: vehicles you view or inquire about, trade-in year/make/model/VIN/mileage/condition, payoff/lien information, purchase type (cash or financing), and service history or requested repairs.",
          "Financial and employment data on credit or pre-qualification forms: income, employer, occupation, housing status, rent/mortgage, credit-related answers, and similar information needed to evaluate financing.",
          "Internet / device data: browser type, pages viewed, referring URL, device identifiers, and approximate location derived from your device or IP address.",
          "Communications: messages you send us, consent records, and records of calls, texts, or emails.",
          "Inferences: interests suggested by browsing or inquiry history (for example, vehicle types you viewed).",
        ],
      },
      {
        title: "3. How we collect information",
        paragraphs: [
          "We collect information you submit on forms (trade-in, service, contact, pre-qualification, and customer-info requests). Full credit applications on /financing are submitted through Cox Automotive / Dealertrack (Finance Driver), not stored as a Drive Now website form. We also collect information automatically when you visit the site, and from service providers who help us operate the site, send messages, store files, or process credit applications.",
          "If you apply for financing, we and participating creditors may obtain consumer reports and check credit and employment history, as authorized in the application you submit.",
        ],
      },
      {
        title: "4. Cookies and automatic collection",
        paragraphs: [
          "We and our providers may use cookies, pixels, and similar technologies to run the site, remember preferences, measure traffic, and understand which inventory or pages are useful.",
          "You can refuse cookies in your browser. Some features may not work if you do. We may also use log files (including IP address) to administer the site and diagnose problems.",
        ],
      },
      {
        title: "5. How we use information",
        paragraphs: [
          "We use personal information to:",
        ],
        bullets: [
          "Respond to inquiries and provide quotes, inventory details, trade-in evaluations, and service scheduling.",
          "Process vehicle purchases, including cash and financed deals, paperwork, e-sign documents, and tax/title/registration.",
          "Submit and support credit applications with lenders and, if approved, service the credit relationship as permitted by law and your authorizations.",
          "Send customer-care texts or calls when you have consented, including appointment and deal updates.",
          "Operate, secure, and improve the website (including fraud prevention and troubleshooting).",
          "Comply with law, court orders, tax and recordkeeping duties, and to protect our rights, customers, and the public.",
          "Send optional marketing only where allowed and, for texts/calls, only with the consent you provided. You may opt out as described below.",
        ],
      },
      {
        title: "6. Credit applications and consumer reports",
        paragraphs: [
          "If you submit a financing or credit-related application (including through the Dealertrack / Cox Finance Driver form on this site), creditors receiving that application may retain it whether or not it is approved and may rely on it in deciding whether to grant credit. We and those creditors may obtain credit reports about you and check credit and employment history as you authorize on the application, including on an ongoing basis during the credit transaction where permitted.",
          "False statements on a credit application may be subject to criminal penalties. If an application is approved, the creditor may share credit information about you with its affiliates as disclosed in the application.",
        ],
      },
      {
        title: "7. SMS, calls, and TCPA consent",
        paragraphs: [
          "If you check the consent box on a form, you consent to receive customer-care text messages and/or phone calls from or on behalf of Drive Now Autohaus or our employees at the number you provided. Message frequency varies. Message and data rates may apply. Consent is not a condition of purchase.",
          "Dealership employees may also opt in to recurring SMS notifications on the account-activation web form (opened from the “Set Password & Activate” email) or later on the Admin Profile page. Those messages may include employee account, lead, appointment, deal, inventory, and operations alerts. Message frequency varies. Message and data rates may apply. Consent is not a condition of employment or of using the admin portal.",
          "We do not sell, rent, or share mobile phone numbers with third parties or affiliates for marketing or promotional purposes. Sharing with vendors who only help us deliver our messages or customer service (for example, our texting or hosting providers, such as Twilio) is permitted.",
          "Text STOP to unsubscribe or HELP for help. You may also ask us to stop by calling (708) 222-6100. Carriers are not liable for delayed or undelivered messages.",
        ],
      },
      {
        title: "8. Who we share information with",
        paragraphs: [
          "We do not sell our customer lists. We do not sell, rent, or share mobile phone numbers with third parties or affiliates for marketing or promotional purposes. We may disclose personal information to:",
        ],
        bullets: [
          "Lenders, finance companies, and other creditors you apply with, and service providers who help process applications or contracts.",
          "Title, registration, tax, insurance, warranty, and other companies needed to complete a sale or service job.",
          "Vendors who host this website, store documents or photos, send email/SMS, provide e-sign, analytics, or IT security — under contracts that limit their use of the data.",
          "Professional advisors (attorneys, accountants, insurers) and authorities when required by law or to protect rights and safety.",
          "A buyer or successor if we sell or reorganize the business, subject to this Policy or comparable protections.",
        ],
        after:
          "In the last twelve months we may have disclosed identifiers, customer-record information, commercial/vehicle information, internet activity, geolocation, employment-related information, and inferences for these business purposes.",
      },
      {
        title: "9. How long we keep information",
        paragraphs: [
          "We keep personal information as long as needed for the purpose collected — including completing a sale or service, servicing financing, resolving disputes, and meeting legal, tax, and recordkeeping requirements (which for dealership and credit files can be several years). We then delete or de-identify it when we no longer have a legitimate need to keep it, unless a longer period is required or permitted by law.",
        ],
      },
      {
        title: "10. Security",
        paragraphs: [
          "We use administrative, technical, and physical safeguards appropriate to the sensitivity of the information, including encryption in transit for forms you submit on this site. No method of transmission or storage is 100% secure. Please use a private device when submitting Social Security numbers or driver’s license details.",
        ],
      },
      {
        title: "11. Children",
        paragraphs: [
          "Our website and dealership services are not directed to children under 16. We do not knowingly collect personal information from children under 16. If you believe a child provided information to us, contact us and we will delete it.",
        ],
      },
      {
        title: "12. Your privacy rights",
        paragraphs: [
          "Depending on where you live, you may have the right to request access to the personal information we hold about you, correction of inaccurate information, deletion, a copy of information you provided, restriction or objection to certain processing, and to opt out of targeted advertising or certain sharing. You also have the right not to be discriminated against for exercising these rights.",
          "Illinois and other state laws may provide additional protections, including notice if certain personal information is acquired in a security breach. California residents have the rights described in the California notice below.",
          "To make a request, email us or call the number in “How to contact us.” We may need to verify your identity before fulfilling the request. We aim to respond within the time required by applicable law.",
        ],
      },
      {
        title: "13. Notice for California residents (CCPA / CPRA)",
        paragraphs: [
          "This section applies to California residents. We do not sell personal information for money. We may disclose personal information to service providers and business partners who are contractually limited in how they use it. If you opt out of “sharing” for cross-context behavioral advertising, we will honor that request.",
          "You may request: the categories and specific pieces of personal information we collected; categories of sources; business or commercial purposes; categories of third parties we disclosed information to; and deletion or correction, subject to legal exceptions (for example, completing a transaction, detecting security incidents, or complying with law).",
          "Authorized agents may submit requests with proof of authorization. We will not deny goods or services, charge a different price, or provide a different quality of service because you exercised CCPA rights.",
        ],
      },
      {
        title: "14. YouTube and other third-party services",
        paragraphs: [
          "Our website may use YouTube or Google services to display video. Use of those features is also subject to YouTube’s Terms of Service and Google’s Privacy Policy. We do not control third-party cookies used by advertisers or social platforms that may appear on or through the site.",
        ],
      },
      {
        title: "15. AI-generated and edited vehicle photos and videos",
        paragraphs: [
          "We may use artificial intelligence (AI) tools, including third-party services, to generate, enhance, or edit photographs and videos of vehicles in our online inventory. That can include adjusting lighting or background, producing additional viewing angles, creating short video clips from still photos, or otherwise preparing listing media.",
          "These images and videos are marketing and listing aids. They are intended to help you understand the vehicle. We do not use AI to hide damage, change equipment, alter mileage, or otherwise deceive buyers about a vehicle’s condition, features, or history.",
          "The vehicle on our lot is the vehicle offered for sale. Please inspect it in person, or have a mechanic you choose inspect it, before you buy. If listing media looks inconsistent with the actual vehicle, contact us and we will correct the listing.",
        ],
        bullets: [
          "AI-edited or AI-generated media may not show every scratch, dent, interior wear, tire condition, aftermarket part, or change made after the media was created.",
          "Color, lighting, reflections, and camera perspective can differ from what you see at the dealership.",
          "Vehicle photos or videos we send to an AI provider are used to produce listing media. We do not use that process to collect extra personal information from website visitors.",
        ],
      },
      {
        title: "16. Where information is processed",
        paragraphs: [
          "This website is hosted on servers in the United States. If you access it from another country, your information will be processed in the United States, which may have different data-protection rules than your home country.",
        ],
      },
      {
        title: "17. Changes to this Policy",
        paragraphs: [
          "We may update this Policy from time to time. The “Last updated” date at the top will change when we do. Continued use of the website after an update means you acknowledge the revised Policy. Material changes that require new consent (for example, for marketing texts) will be presented to you before we rely on that new use.",
        ],
      },
      {
        title: "18. How to contact us",
        paragraphs: [
          "For privacy questions, to exercise your rights, or to opt out of marketing emails or texts:",
        ],
        bullets: [
          "Drive Now Autohaus, 5401 W Cermak Rd, Cicero, IL 60804",
          "Phone: (708) 222-6100",
          "In person at the dealership during business hours",
          "For SMS: text STOP to opt out or HELP for help",
        ],
        after:
          "Please include your name, the phone or email you used with us, and a description of your request so we can locate your information.",
      },
    ],
  },
  es: {
    tagline: "Su privacidad",
    title: "Política de Privacidad",
    intro:
      "Drive Now Autohaus (“nosotros”) respeta su privacidad. Esta Política explica qué información personal recopilamos en este sitio web y en relación con ventas, financiamiento, recibo de vehículos a cuenta y servicio; cómo la usamos; con quién la compartimos; y qué opciones tiene. Al usar este sitio o enviar un formulario, usted reconoce esta Política.",
    dealerBlock: [
      "Drive Now Autohaus",
      "5401 W Cermak Rd, Cicero, IL 60804",
      "Teléfono: (708) 222-6100",
    ],
    sections: [
      {
        title: "1. Alcance",
        paragraphs: [
          "Esta Política aplica a nuestro sitio web, inventario en línea, formularios de financiamiento y precalificación, solicitudes de recibo de vehículo, citas de servicio, solicitudes de información del cliente, firma electrónica, mensajes SMS/llamadas y operaciones relacionadas del concesionario.",
          "No aplica a sitios de terceros a los que enlacemos (prestamistas, fabricantes, Google, YouTube, Meta o procesadores de pago). Esas empresas tienen sus propias políticas.",
        ],
      },
      {
        title: "2. Información personal que recopilamos",
        paragraphs: [
          "Podemos recopilar información que lo identifique o pueda vincularse razonablemente con usted, incluyendo:",
        ],
        bullets: [
          "Identificadores: nombre, correo, teléfono, dirección, ciudad, estado, código postal, dirección IP e identificadores de solicitud.",
          "Datos de identidad que usted proporcione: fecha de nacimiento, licencia de conducir, estado/condado emisor, vencimiento y número de Seguro Social (normalmente en solicitudes de crédito).",
          "Datos comerciales y del vehículo: vehículos que consulta, información de recibo (año/marca/modelo/VIN/millaje), gravámenes/pago restante, tipo de compra y solicitudes de servicio.",
          "Datos financieros y laborales en solicitudes de crédito o precalificación: ingresos, empleador, ocupación, vivienda, renta/hipoteca y respuestas relacionadas con el crédito.",
          "Datos de internet/dispositivo: navegador, páginas vistas, URL de origen, identificadores del dispositivo y ubicación aproximada.",
          "Comunicaciones: mensajes que nos envía y registros de consentimiento, llamadas, textos o correos.",
          "Inferencias basadas en su navegación o consultas.",
        ],
      },
      {
        title: "3. Cómo recopilamos la información",
        paragraphs: [
          "Recopilamos lo que usted envía en formularios (recibo de vehículo, servicio, contacto, precalificación y solicitudes de información). Las solicitudes de crédito completas en /financing se envían a través de Cox Automotive / Dealertrack (Finance Driver) y no se almacenan como un formulario propio de Drive Now. También recopilamos datos de visita al sitio y de proveedores que nos ayudan a operar el sitio, enviar mensajes, guardar archivos o procesar crédito.",
          "Si solicita financiamiento, nosotros y los acreedores participantes podemos obtener informes de crédito y verificar empleo, según usted lo autorice en la solicitud.",
        ],
      },
      {
        title: "4. Cookies y recopilación automática",
        paragraphs: [
          "Nosotros y nuestros proveedores podemos usar cookies y tecnologías similares para operar el sitio, recordar preferencias y medir el tráfico. Puede rechazar cookies en su navegador; algunas funciones podrían no funcionar.",
        ],
      },
      {
        title: "5. Cómo usamos la información",
        paragraphs: [
          "Usamos la información para responder consultas; procesar compras, documentos y e-sign; presentar solicitudes de crédito; enviar textos o llamadas de atención al cliente cuando haya consentido; operar y proteger el sitio; cumplir la ley; y, solo cuando esté permitido, marketing, del cual puede darse de baja.",
        ],
      },
      {
        title: "6. Solicitudes de crédito e informes del consumidor",
        paragraphs: [
          "Si envía una solicitud de financiamiento o crédito (incluso mediante el formulario Dealertrack / Cox Finance Driver en este sitio), los acreedores que la reciban pueden conservarla aunque no se apruebe y basarse en ella para decidir el crédito. Podemos obtener informes de crédito y verificar historial según su autorización. Las declaraciones falsas pueden conllevar sanciones penales.",
        ],
      },
      {
        title: "7. SMS, llamadas y consentimiento",
        paragraphs: [
          "Si marca la casilla de consentimiento, acepta recibir mensajes de texto y/o llamadas de atención al cliente de Drive Now Autohaus o nuestros empleados al número que indicó. La frecuencia varía. Pueden aplicar tarifas de mensajes y datos. El consentimiento no es condición de compra.",
          "Los empleados del concesionario también pueden optar por notificaciones SMS recurrentes en el formulario web de activación de cuenta (el enlace “Set Password & Activate” del correo) o más tarde en el Perfil de administración. Esos mensajes pueden incluir alertas de cuenta, prospectos, citas, tratos, inventario y operaciones. La frecuencia varía. Pueden aplicar tarifas. El consentimiento no es condición de empleo ni del uso del portal.",
          "No vendemos, alquilamos ni compartimos números móviles con terceros o afiliados para mercadeo o promoción. El uso de proveedores que solo envían nuestros mensajes (por ejemplo, Twilio) está permitido. Envíe STOP para cancelar o HELP para ayuda. También puede llamar al (708) 222-6100.",
        ],
      },
      {
        title: "8. Con quién compartimos información",
        paragraphs: [
          "No vendemos nuestras listas de clientes ni números móviles para mercadeo de terceros. Podemos divulgar información a prestamistas, compañías de título/impuestos/seguro/garantía, proveedores tecnológicos (alojamiento, documentos, SMS, e-sign), asesores y autoridades cuando la ley lo exija, o en una venta del negocio.",
        ],
      },
      {
        title: "9. Conservación",
        paragraphs: [
          "Conservamos la información el tiempo necesario para la venta, el servicio, el financiamiento, disputas y obligaciones legales o fiscales; luego la eliminamos o desidentificamos cuando ya no exista una necesidad legítima.",
        ],
      },
      {
        title: "10. Seguridad",
        paragraphs: [
          "Usamos medidas administrativas, técnicas y físicas razonables, incluida cifrado en tránsito en los formularios del sitio. Ningún sistema es 100% seguro.",
        ],
      },
      {
        title: "11. Menores",
        paragraphs: [
          "Este sitio no está dirigido a menores de 16 años y no recopilamos conscientemente su información. Contáctenos si cree que un menor nos proporcionó datos.",
        ],
      },
      {
        title: "12. Sus derechos de privacidad",
        paragraphs: [
          "Según su estado de residencia, puede solicitar acceso, corrección, eliminación, una copia de sus datos, o oponerse a cierta publicidad dirigida. No lo discriminaremos por ejercer estos derechos. Para solicitudes, use los datos de contacto de la sección 18.",
        ],
      },
      {
        title: "13. Aviso para residentes de California (CCPA / CPRA)",
        paragraphs: [
          "No vendemos información personal a cambio de dinero. Los residentes de California pueden solicitar categorías y datos específicos recopilados, fuentes, fines, terceros y, sujeto a excepciones legales, eliminación o corrección. Los agentes autorizados pueden presentar solicitudes con prueba de autorización.",
        ],
      },
      {
        title: "14. YouTube y terceros",
        paragraphs: [
          "El sitio puede usar servicios de YouTube o Google. Esos servicios se rigen también por sus propios términos y políticas de privacidad.",
        ],
      },
      {
        title: "15. Fotos y videos de vehículos generados o editados con IA",
        paragraphs: [
          "Podemos usar herramientas de inteligencia artificial (IA), incluidos servicios de terceros, para generar, mejorar o editar fotografías y videos de los vehículos de nuestro inventario en línea. Eso puede incluir ajustar iluminación o fondo, crear ángulos adicionales, producir clips de video a partir de fotos, u otro trabajo de medios para el anuncio.",
          "Esas imágenes y videos son ayudas de mercadeo y de listado. Su propósito es ayudarle a conocer el vehículo. No usamos IA para ocultar daños, cambiar el equipamiento, alterar el millaje ni para engañar a los compradores sobre la condición, las características o el historial de un vehículo.",
          "El vehículo en nuestro lote es el vehículo que se ofrece a la venta. Inspecciónelo en persona, o haga que un mecánico de su elección lo inspeccione, antes de comprar. Si los medios del anuncio no coinciden con el vehículo real, contáctenos y corregiremos el listado.",
        ],
        bullets: [
          "Los medios editados o generados con IA pueden no mostrar cada rayón, abolladura, desgaste interior, estado de las llantas, pieza de posventa o cambio posterior a la creación del medio.",
          "El color, la iluminación, los reflejos y la perspectiva de la cámara pueden diferir de lo que verá en el concesionario.",
          "Las fotos o videos del vehículo que enviamos a un proveedor de IA se usan para producir medios del anuncio. No usamos ese proceso para recopilar información personal adicional de los visitantes del sitio.",
        ],
      },
      {
        title: "16. Dónde se procesa la información",
        paragraphs: [
          "Este sitio se aloja en servidores en Estados Unidos.",
        ],
      },
      {
        title: "17. Cambios a esta Política",
        paragraphs: [
          "Podemos actualizar esta Política. La fecha de “Última actualización” cambiará cuando lo hagamos. El uso continuado del sitio implica el reconocimiento de la versión revisada.",
        ],
      },
      {
        title: "18. Cómo contactarnos",
        paragraphs: [
          "Drive Now Autohaus, 5401 W Cermak Rd, Cicero, IL 60804. Teléfono: (708) 222-6100. Para SMS: envíe STOP para cancelar o HELP para ayuda. Incluya su nombre y el teléfono o correo que usó con nosotros.",
        ],
      },
    ],
  },
};
